import { EffectRandom } from '../game/effects/random';
import type { WorldSetupState } from '../game/world';
import type { Camera } from './camera';

const COUNT = 5;
const PARALLAX = 0.2;
export const NEBULA_PARALLAX = PARALLAX;
const VIEW_SPAN = 2400;
const EDGE = 400;
const SPRITE_PX = 512;
const MIN_RADIUS = 520;
const MAX_RADIUS = 1200;
const MIN_ALPHA = 0.5;
const MAX_ALPHA = 0.85;
const BOLD_SHARE = 0.35;
const BOLD_MIN_ALPHA = 1.05;
const BOLD_MAX_ALPHA = 1.4;
const BOLD_GAIN = 1.5;
const HUES = [208, 262, 318, 176] as const;
const SPACING = 0.6;
const PLACEMENT_TRIES = 40;
const SWAP_MS = 900;
const TAU = Math.PI * 2;
/** The clouds are a 512px source already stretched three to five times over, so the pass that composites them has resolution to give up before the picture does. */
const HALF = 0.5;

const OCTAVES = [
  { count: 5, spread: 0.5, size: 0.44, alpha: 0.085 },
  { count: 16, spread: 0.76, size: 0.2, alpha: 0.075 },
  { count: 40, spread: 0.9, size: 0.1, alpha: 0.062 },
  { count: 90, spread: 0.95, size: 0.045, alpha: 0.05 },
] as const;

const HOLES = 22;

export interface NebulaSpec {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly alpha: number;
  readonly hue: number;
  readonly bold: boolean;
}

export function nebulaSeed(world: WorldSetupState): number {
  let hash = 0x811c9dc5;
  for (const value of [world.maxX, world.maxY, world.baseDistance, world.baseAngle * 4096]) {
    hash = Math.imul(hash ^ (Math.round(value) | 0), 0x01000193) >>> 0;
  }
  return hash;
}

export function nebulaLayout(world: WorldSetupState): NebulaSpec[] {
  const random = new EffectRandom(nebulaSeed(world));
  const specs: NebulaSpec[] = [];
  const spanX = world.maxX * PARALLAX + VIEW_SPAN;
  const spanY = world.maxY * PARALLAX + VIEW_SPAN;
  for (let index = 0; index < COUNT; index += 1) {
    const radius = random.range(MIN_RADIUS, MAX_RADIUS);
    let x = 0;
    let y = 0;
    for (let attempt = 0; attempt < PLACEMENT_TRIES; attempt += 1) {
      x = random.range(-EDGE, spanX - EDGE);
      y = random.range(-EDGE, spanY - EDGE);
      const clear = specs.every(
        (other) => Math.hypot(other.x - x, other.y - y) > (other.radius + radius) * SPACING,
      );
      if (clear) break;
    }
    const bold = random.next() < BOLD_SHARE;
    specs.push({
      x,
      y,
      radius,
      alpha: bold
        ? random.range(BOLD_MIN_ALPHA, BOLD_MAX_ALPHA)
        : random.range(MIN_ALPHA, MAX_ALPHA),
      hue: HUES[Math.floor(random.next() * HUES.length)] ?? HUES[0],
      bold,
    });
  }
  return specs;
}

function paint(spec: NebulaSpec, seed: number): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_PX;
  canvas.height = SPRITE_PX;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  const random = new EffectRandom(seed);
  const mid = SPRITE_PX / 2;
  const gain = spec.bold ? BOLD_GAIN : 1;
  ctx.globalCompositeOperation = 'lighter';
  for (const octave of OCTAVES) {
    for (let stamp = 0; stamp < octave.count; stamp += 1) {
      const angle = random.range(0, TAU);
      const distance = Math.sqrt(random.next()) * mid * octave.spread;
      const x = mid + Math.cos(angle) * distance;
      const y = mid + Math.sin(angle) * distance;
      const radius = mid * octave.size * random.range(0.6, 1.4);
      const hue = String(spec.hue + random.range(-22, 22));
      const core = octave.alpha * gain;
      const blob = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
      blob.addColorStop(0, `hsla(${hue}, 80%, ${spec.bold ? '70%' : '64%'}, ${String(core)})`);
      blob.addColorStop(0.5, `hsla(${hue}, 85%, 46%, ${String(core * 0.45)})`);
      blob.addColorStop(1, `hsla(${hue}, 90%, 30%, 0)`);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(random.range(0, TAU));
      ctx.scale(1, random.range(0.3, 0.85));
      ctx.fillStyle = blob;
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
  }
  ctx.globalCompositeOperation = 'destination-out';
  for (let hole = 0; hole < HOLES; hole += 1) {
    const angle = random.range(0, TAU);
    const distance = Math.sqrt(random.next()) * mid * 0.9;
    const x = mid + Math.cos(angle) * distance;
    const y = mid + Math.sin(angle) * distance;
    const radius = mid * random.range(0.04, 0.16);
    const lane = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
    lane.addColorStop(0, `rgba(0, 0, 0, ${String(random.range(0.25, 0.6))})`);
    lane.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(random.range(0, TAU));
    ctx.scale(1, random.range(0.25, 0.6));
    ctx.fillStyle = lane;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, TAU);
    ctx.fill();
    ctx.restore();
  }

  ctx.globalCompositeOperation = 'destination-in';
  const mask = ctx.createRadialGradient(mid, mid, mid * 0.15, mid, mid, mid);
  mask.addColorStop(0, 'rgba(0, 0, 0, 1)');
  mask.addColorStop(0.62, 'rgba(0, 0, 0, 0.8)');
  mask.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = mask;
  ctx.fillRect(0, 0, SPRITE_PX, SPRITE_PX);
  return canvas;
}

interface Cloud {
  readonly spec: NebulaSpec;
  readonly sprite: HTMLCanvasElement;
}

export function nebulaSprites(world: WorldSetupState): Cloud[] {
  const seed = nebulaSeed(world);
  return nebulaLayout(world).flatMap((spec, index) => {
    const sprite = paint(spec, (seed ^ Math.imul(index + 1, 0x9e3779b9)) >>> 0);
    return sprite === null ? [] : [{ spec, sprite }];
  });
}

export class NebulaField {
  private half: HTMLCanvasElement | null = null;
  private key: number | null = null;
  private clouds: Cloud[] = [];
  private outgoing: Cloud[] = [];
  private swapAt = 0;
  private swapping = false;

  /** The idle menu's 1fps loop would sample a 900ms fade twice and show it as a brightness jump — must stay off until this clears. */
  get busy(): boolean {
    return this.swapping;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    world: WorldSetupState,
    alpha: number,
    now: number,
  ): void {
    if (alpha <= 0) return;
    this.ensure(world, now);
    const swap = Math.min(1, (now - this.swapAt) / SWAP_MS);
    this.swapping = swap < 1;
    if (swap >= 1) this.outgoing = [];
    const scratch = this.scratch(camera);
    if (scratch === null) {
      this.drawSet(this.outgoing, ctx, camera, alpha * (1 - swap));
      this.drawSet(this.clouds, ctx, camera, alpha * swap);
      return;
    }
    scratch.setTransform(1, 0, 0, 1, 0, 0);
    scratch.globalCompositeOperation = 'copy';
    scratch.globalAlpha = 1;
    scratch.fillStyle = 'rgba(0,0,0,0)';
    scratch.fillRect(0, 0, scratch.canvas.width, scratch.canvas.height);
    scratch.globalCompositeOperation = 'source-over';
    scratch.scale(HALF * camera.worldScale, HALF * camera.worldScale);
    scratch.translate(-camera.offsetX, -camera.offsetY);
    this.drawSet(this.outgoing, scratch, camera, alpha * (1 - swap));
    this.drawSet(this.clouds, scratch, camera, alpha * swap);

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 1;
    ctx.drawImage(scratch.canvas, 0, 0, camera.deviceWidth, camera.deviceHeight);
    ctx.restore();
  }

  private scratch(camera: Camera): CanvasRenderingContext2D | null {
    if (typeof document === 'undefined') return null;
    const width = Math.max(1, Math.round(camera.deviceWidth * HALF));
    const height = Math.max(1, Math.round(camera.deviceHeight * HALF));
    if (this.half === null || this.half.width !== width || this.half.height !== height) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      this.half = canvas;
    }
    return this.half.getContext('2d');
  }

  private drawSet(
    clouds: readonly Cloud[],
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    alpha: number,
  ): void {
    if (clouds.length === 0 || alpha <= 0) return;
    const left = camera.offsetX * PARALLAX;
    const top = camera.offsetY * PARALLAX;
    const right = left + camera.worldWidth;
    const bottom = top + camera.worldHeight;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.translate(camera.offsetX * (1 - PARALLAX), camera.offsetY * (1 - PARALLAX));
    for (const { spec, sprite } of clouds) {
      const reach = spec.radius;
      if (spec.x + reach < left || spec.x - reach > right) continue;
      if (spec.y + reach < top || spec.y - reach > bottom) continue;
      ctx.globalAlpha = spec.alpha * alpha;
      ctx.drawImage(
        sprite,
        spec.x - spec.radius,
        spec.y - spec.radius,
        spec.radius * 2,
        spec.radius * 2,
      );
    }
    ctx.restore();
  }

  private ensure(world: WorldSetupState, now: number): void {
    const seed = nebulaSeed(world);
    if (seed === this.key) return;
    this.outgoing = this.clouds;
    this.swapAt = now;
    this.key = seed;
    this.clouds = nebulaSprites(world);
  }
}
